import { Link } from "react-router-dom";
import HeroCarousel from "./components/HeroCarousel";
import SectionHeading from "@/components/feature/SectionHeading";
import MovieCard from "@/components/feature/MovieCard";
import NovelCard from "@/components/feature/NovelCard";
import NewsListItem from "@/components/feature/NewsListItem";
import { allMovies, allNovels, allNews, allSlides } from "@/utils/catalog";

const CATEGORY_ENTRIES = [
  {
    to: "/category/movie",
    title: "影视剧",
    desc: "科幻、悬疑、奇幻、年代……精选热门影视剧集，一屏纵览。",
    image:
      "https://readdy.ai/api/search-image?query=cinema%20hall%20with%20red%20seats%20and%20a%20glowing%20screen%2C%20teal%20ambient%20light%20with%20warm%20orange%20accents%2C%20cinematic%20mood%2C%20high%20detail&width=800&height=560&seq=home-cat-movie&orientation=landscape",
    icon: "ri-film-line",
    count: "1000+ 部",
  },
  {
    to: "/category/novel",
    title: "小说阅读",
    desc: "玄幻、都市、科幻、言情……海量佳作在线阅读，随时随地。",
    image:
      "https://readdy.ai/api/search-image?query=cozy%20reading%20corner%20with%20books%20and%20warm%20lamp%20by%20a%20window%2C%20teal%20tones%20with%20orange%20light%2C%20calm%20literary%20mood%2C%20high%20detail&width=800&height=560&seq=home-cat-novel&orientation=landscape",
    icon: "ri-book-open-line",
    count: "5000+ 本",
  },
  {
    to: "/category/news",
    title: "娱乐资讯",
    desc: "影视资讯、明星动态、栏目综述，最新娱乐新闻每日更新。",
    image:
      "https://readdy.ai/api/search-image?query=modern%20newsroom%20with%20screens%20and%20soft%20studio%20lighting%2C%20teal%20and%20warm%20orange%20tones%2C%20editorial%20mood%2C%20high%20detail&width=800&height=560&seq=home-cat-news&orientation=landscape",
    icon: "ri-newspaper-line",
    count: "每日更新",
  },
];

export default function Home() {
  const hotMovies = [...allMovies].sort((a, b) => b.heat - a.heat).slice(0, 6);
  const hotNovels = [...allNovels].sort((a, b) => b.recommend - a.recommend).slice(0, 4);
  const latestNews = [...allNews].slice(0, 5);

  return (
    <div className="w-full">
      <HeroCarousel slides={allSlides} />

      {/* Category quick entries */}
      <section className="w-full px-4 md:px-8 py-10 md:py-14 bg-background-50">
        <SectionHeading
          eyebrow="快速入口"
          title="按内容分类浏览"
          subtitle="影视、小说、资讯三大频道，点击卡片直达对应分类"
        />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
          {CATEGORY_ENTRIES.map((item) => (
            <Link
              key={item.to}
              to={item.to}
              className="group relative rounded-lg overflow-hidden border border-background-200 cursor-pointer"
            >
              <div className="relative w-full h-44 md:h-48 overflow-hidden bg-background-200">
                <img
                  src={item.image}
                  alt={item.title}
                  title={`${item.title}分类`}
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/80 via-foreground-950/20 to-transparent"></div>
              </div>
              <div className="absolute bottom-0 left-0 right-0 p-4 md:p-5">
                <div className="flex items-center gap-2 mb-1.5">
                  <span className="w-8 h-8 rounded-md bg-accent-500 text-foreground-950 flex items-center justify-center">
                    <i className={`${item.icon} text-lg`}></i>
                  </span>
                  <h3 className="font-heading text-xl font-black text-background-50">
                    {item.title}
                  </h3>
                  <span className="ml-auto px-2 py-0.5 rounded-full text-xs font-label bg-background-50/20 text-background-50">
                    {item.count}
                  </span>
                </div>
                <p className="text-sm text-background-200 line-clamp-2 leading-relaxed">
                  {item.desc}
                </p>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* Hot movies */}
      <section className="w-full px-4 md:px-8 py-10 md:py-14 bg-background-100">
        <SectionHeading
          eyebrow="热门影视"
          title="本周热播影视推荐"
          subtitle="根据站内热度整理，覆盖科幻、悬疑、奇幻等热门类型"
          moreTo="/category/movie"
        />
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 md:gap-5">
          {hotMovies.map((movie) => (
            <MovieCard key={movie.id} movie={movie} />
          ))}
        </div>
      </section>

      {/* Hot novels */}
      <section className="w-full px-4 md:px-8 py-10 md:py-14 bg-background-50">
        <SectionHeading
          eyebrow="热门小说"
          title="书友都在追的佳作"
          subtitle="玄幻、都市、科幻、言情……总有一本适合你"
          moreTo="/category/novel"
        />
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-5">
          {hotNovels.map((novel) => (
            <NovelCard key={novel.id} novel={novel} />
          ))}
        </div>
      </section>

      {/* Latest news */}
      <section className="w-full px-4 md:px-8 py-10 md:py-14 bg-background-100">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 md:gap-8">
          <div className="lg:col-span-2">
            <SectionHeading
              eyebrow="娱乐资讯"
              title="最新娱乐资讯"
              subtitle="影视动态、明星资讯与行业综述，第一时间掌握"
              moreTo="/category/news"
            />
            <div className="rounded-lg border border-background-200 bg-background-50 px-4 md:px-5">
              {latestNews.map((item) => (
                <NewsListItem key={item.id} item={item} />
              ))}
            </div>
          </div>

          <div className="lg:col-span-1">
            <div className="h-full rounded-lg bg-secondary-900 text-background-100 p-6 md:p-7 flex flex-col">
              <span className="inline-flex items-center gap-1.5 text-xs font-label font-bold uppercase tracking-wider text-accent-400 mb-3">
                <i className="ri-information-line"></i>
                关于我们
              </span>
              <h2 className="font-heading text-2xl font-black text-background-50 leading-snug">
                17C.COM 综合娱乐平台
              </h2>
              <p className="mt-4 text-sm leading-relaxed text-background-200">
                17C.COM 是一家综合性娱乐平台，聚合影视剧展示、小说在线阅读与最新娱乐资讯三大内容板块。无论你是追剧达人、小说爱好者，还是娱乐新闻的关注者，都能在这里快速找到感兴趣的内容。
              </p>
              <div className="mt-6 grid grid-cols-3 gap-3">
                <div>
                  <p className="font-heading text-2xl font-black text-accent-400">1000+</p>
                  <p className="text-xs text-background-300 mt-1">影视作品</p>
                </div>
                <div>
                  <p className="font-heading text-2xl font-black text-accent-400">5000+</p>
                  <p className="text-xs text-background-300 mt-1">小说资源</p>
                </div>
                <div>
                  <p className="font-heading text-2xl font-black text-accent-400">每日</p>
                  <p className="text-xs text-background-300 mt-1">资讯更新</p>
                </div>
              </div>
              <Link
                to="/search"
                className="mt-auto pt-6 inline-flex items-center gap-2 text-sm font-medium text-primary-300 hover:text-accent-400 transition-colors cursor-pointer"
              >
                前往内容搜索
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}